import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Phone, Mail, ArrowUpRight } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/motion/Reveal";
import { CTASection } from "@/components/CTASection";
import { TEAM, BUSINESS } from "@/data/content";

export default function Team() {
  const founder = TEAM[0];
  return (
    <div data-testid="team-page">
      <PageHeader
        overline="Meet the Owner"
        titleLines={["One person.", "Fully invested."]}
        subtitle="When you work with Walk-Off Digital, you work directly with the owner — no account managers, no hand-offs. Just a real person invested in your growth."
      />

      <section className="bg-white py-24 sm:py-32">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-5">
              <Reveal>
                <div
                  data-testid="team-member-0"
                  className="group relative mx-auto max-w-md overflow-hidden rounded-3xl border border-black/10 bg-ink"
                >
                  <div className="relative aspect-[4/5] overflow-hidden">
                    <motion.img
                      src={founder.img}
                      alt={founder.name}
                      className="h-full w-full object-cover"
                      initial={{ scale: 1.06 }}
                      whileInView={{ scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 1 }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
                    <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between">
                      <div>
                        <p className="font-display text-2xl font-bold tracking-tight text-white">
                          {founder.name}
                        </p>
                        <p className="font-mono text-xs uppercase tracking-[0.2em] text-brand">
                          {founder.role}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-7">
              <Reveal delay={0.1}>
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-brand-dark">
                  {BUSINESS.motto}
                </p>
                <h2 className="mt-5 font-display text-4xl font-extrabold leading-[0.98] tracking-tight sm:text-5xl">
                  Hi, I'm {founder.name.split(" ")[0]}.
                </h2>
                <p className="mt-6 max-w-xl text-lg leading-relaxed text-ink/65">
                  I am from New Orleans, Louisiana. Growing up in New Orleans, and playing
                  college baseball at Delgado Community College and Houston Christian
                  University, it has taught me a lot of life lessons. It has taught me how
                  to want to help people and how to work as a team. I have learned
                  leadership skills, ways to problem solve, and how to work as a team to
                  better your company. This is why I started this business and will
                  continue to grow it. I am looking to work with small businesses and help
                  grow them through updating their websites, bringing them more clients,
                  and being personable and transparent through the process. I hope to work
                  with you soon!
                </p>

                <div className="mt-9 flex flex-col gap-4 sm:flex-row">
                  <Link
                    to="/contact"
                    data-testid="team-cta"
                    className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand px-7 py-4 font-bold text-ink transition-transform duration-300 hover:-translate-y-0.5 hover:bg-brand-dark hover:text-white"
                  >
                    Work with me <ArrowUpRight className="h-4 w-4" />
                  </Link>
                  <a
                    href={BUSINESS.phoneHref}
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-black/15 px-7 py-4 font-bold text-ink transition-colors hover:bg-paper"
                  >
                    <Phone className="h-4 w-4 text-brand-dark" /> {BUSINESS.phone}
                  </a>
                </div>

                <a
                  href={BUSINESS.emailHref}
                  className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-ink/60 hover:text-brand-dark"
                >
                  <Mail className="h-4 w-4" /> {BUSINESS.email}
                </a>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
